import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Play } from 'lucide-react';

export interface HlsVideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  muted?: boolean;
  className?: string;
  onError?: (err: string) => void;
  onLoaded?: () => void;
}

const HLS_DEBUG = false;

/**
 * HLS video player sa native iOS/Safari fallback-om i `hls.js` za Chromium/Firefox.
 *
 * - Native HLS: Safari, iOS WebView (canPlayType 'application/vnd.apple.mpegurl')
 * - hls.js: Chrome, Firefox, Android System WebView (Chromium-based)
 * - Auto-recovery: NETWORK_ERROR → startLoad(), MEDIA_ERROR → recoverMediaError()
 * - Low latency mode (manje buffering-a, brži join time za live)
 *
 * Tap-to-play overlay:
 *   Browseri (posebno Chromium na desktop-u i Android-u) odbijaju unmuted
 *   autoPlay() kad nije bilo user gesture-a u tom path-u — radio sluša fail
 *   tiho i ekran ostaje siv. `onPlay`/`onPlaying` događaji potvrđuju da
 *   reprodukcija stvarno teče; ako autoplay rejection-i dođu prije njih,
 *   prikazujemo veliki Play overlay koji jednim tap-om resume-a stream.
 */
export const HlsVideoPlayer: React.FC<HlsVideoPlayerProps> = ({
  src,
  autoPlay = true,
  muted = false,
  className = '',
  onError,
  onLoaded,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsTapToPlay, setNeedsTapToPlay] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    setIsLoading(true);
    setNeedsTapToPlay(false);

    const onPlaying = () => setNeedsTapToPlay(false);
    video.addEventListener('playing', onPlaying);

    const tryAutoPlay = (): void => {
      if (!autoPlay) return;
      const p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          setNeedsTapToPlay(false);
        }).catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'autoplay rejected';
          if (HLS_DEBUG) console.warn('[HLS] Autoplay blocked:', msg);
          setNeedsTapToPlay(true);
        });
      }
    };

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      const onLoadedMeta = () => {
        setIsLoading(false);
        onLoaded?.();
        tryAutoPlay();
      };
      video.addEventListener('loadedmetadata', onLoadedMeta, { once: true });
      return () => {
        video.removeEventListener('loadedmetadata', onLoadedMeta);
        video.removeEventListener('playing', onPlaying);
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch {
          /* noop */
        }
      };
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        lowLatencyMode: true,
        backBufferLength: 30,
        maxBufferLength: 10,
        liveSyncDurationCount: 3,
      });
      hlsRef.current = hls;

      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
        onLoaded?.();
        tryAutoPlay();
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (HLS_DEBUG) console.warn('[HLS] error', data);
        if (!data.fatal) return;
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            try { hls.startLoad(); } catch { /* noop */ }
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            try { hls.recoverMediaError(); } catch { /* noop */ }
            break;
          default:
            onError?.(`HLS error: ${data.details}`);
            try { hls.destroy(); } catch { /* noop */ }
            break;
        }
      });

      return () => {
        video.removeEventListener('playing', onPlaying);
        try { hls.destroy(); } catch { /* noop */ }
        hlsRef.current = null;
      };
    }

    onError?.('HLS not supported on this device');
    setIsLoading(false);
    return () => {
      video.removeEventListener('playing', onPlaying);
    };
  }, [src, autoPlay, onError, onLoaded]);

  const handleTapToPlay = (): void => {
    const video = videoRef.current;
    if (!video) return;
    const p = video.play();
    if (p && typeof p.then === 'function') {
      p.then(() => setNeedsTapToPlay(false)).catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'play rejected';
        onError?.(msg);
      });
    } else {
      setNeedsTapToPlay(false);
    }
  };

  return (
    <div className={`relative w-full bg-black ${className}`}>
      <video
        ref={videoRef}
        className="w-full h-full"
        style={{ objectFit: 'contain' }}
        controls
        playsInline
        autoPlay={autoPlay}
        muted={muted}
      />
      {isLoading && !needsTapToPlay && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40">
          <div className="text-white text-sm animate-pulse">…</div>
        </div>
      )}
      {needsTapToPlay && (
        <button
          type="button"
          onClick={handleTapToPlay}
          aria-label="Play"
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 transition-colors hover:bg-black/70"
        >
          <span className="w-20 h-20 rounded-full bg-red-500/90 flex items-center justify-center shadow-lg">
            <Play className="w-10 h-10 text-white ml-1" />
          </span>
        </button>
      )}
    </div>
  );
};
